import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { requireSupabaseConfig } from "./env";

const AUTH_STORAGE_KEY = "tams-admin-auth-token";
const REMEMBER_KEY = "tams-admin-remember";

let client: SupabaseClient | null = null;

function hasAuthToken() {
	return (
		window.localStorage.getItem(AUTH_STORAGE_KEY) != null ||
		window.sessionStorage.getItem(AUTH_STORAGE_KEY) != null
	);
}

/**
 * Session token may live in localStorage (remember) or sessionStorage.
 * REMEMBER_KEY is a UX preference and is not cleared with the session.
 */
const authStorage: Storage = {
	get length() {
		return hasAuthToken() ? 1 : 0;
	},
	clear() {
		window.localStorage.removeItem(AUTH_STORAGE_KEY);
		window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
	},
	key(index) {
		if (index !== 0 || !hasAuthToken()) return null;
		return AUTH_STORAGE_KEY;
	},
	getItem(key) {
		return (
			window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key)
		);
	},
	setItem(key, value) {
		const remember = window.localStorage.getItem(REMEMBER_KEY) !== "0";
		if (remember) {
			window.localStorage.setItem(key, value);
			window.sessionStorage.removeItem(key);
		} else {
			window.sessionStorage.setItem(key, value);
			window.localStorage.removeItem(key);
		}
	},
	removeItem(key) {
		window.localStorage.removeItem(key);
		window.sessionStorage.removeItem(key);
	},
};

export function setRememberSession(remember: boolean) {
	window.localStorage.setItem(REMEMBER_KEY, remember ? "1" : "0");
}

export function getSupabaseClient() {
	if (client) return client;
	const { url, anonKey } = requireSupabaseConfig();
	client = createClient(url, anonKey, {
		auth: {
			storage: authStorage,
			storageKey: AUTH_STORAGE_KEY,
			autoRefreshToken: true,
			persistSession: true,
			detectSessionInUrl: false,
			flowType: "pkce",
		},
	});
	return client;
}
